import { Injectable } from '@angular/core';
import { Directory, PlanningItem, RegionRef } from '@mocca-technologies/model';

@Injectable({
    providedIn: 'root'
})
export class DirectoryService {

    /**
     * This method is used to convert a list of PlanningItems into a list of folders.
     * @param elementList is a list of PlanningItems (OpRequests, regions...)
     * @returns a Directory array with all the folders and items.
     */
    buildTree(elementList: PlanningItem[], regionMapFlag?: Boolean): Directory[] {
        const tree: Directory[] = [];

        elementList.forEach((element) => {

            let currentDirectory: Directory = null;
            let parentDirectory: Directory = null;

            // In case the path is a simple path (it is in the home directory, therefore it won't have parentRef)
            if(element.path && element.path==="/"){
                currentDirectory = {
                    selected: false,
                    pseudonym: "",
                    name: "",
                    level: 0,
                    isExpanded: false,
                    badgeValue: 0,
                    parentRef: null,
                    items: []
                };
                tree.push(currentDirectory);
                currentDirectory.items.push(element);
                element.directoryRef = currentDirectory;
            }
            // In case it is a normal path, not in the home directory
            else if(element.path){
                // If the path doesn't start with "/", we add it. ALL PATH TREATED MUST START WITH "/"
                if(!element.path.startsWith('/')){
                    element.path = "/"+element.path;
                }



                element.path.split('/').forEach((curr, idx) => {

                    // Revise if the path is empty
                    if(curr!==''){
                        //We check if all the parent directories are found, the last added will be the parent from the new directory
                        if (idx !== 0) {

                            currentDirectory = tree.find((dir) => {
                                if (dir?.parentRef !== null){
                                    return dir?.name === curr && dir?.level === idx && dir?.parentRef?.name === element?.path.split('/')[idx-1];
                                } else{
                                    return dir?.name === curr && dir?.level === idx;
                                }

                            });
                        } else{
                            currentDirectory = tree.find((dir) => dir.name === curr && dir.level === idx);
                        }


                        //If we don't find the directory we add it
                        if (!currentDirectory) {
                            currentDirectory = {
                                pseudonym: curr,
                                selected: false,
                                name: curr,
                                level: idx,
                                isExpanded: false,
                                badgeValue: 0,
                                parentRef: parentDirectory,
                                items: []
                            };

                            tree.push(currentDirectory);
                        }

                        //We update the parent directory to the latest one
                        parentDirectory = currentDirectory;
                    }
                });

                // We update the element items with the directory reference and the kind. This is done depending on the Planning Item type.
                if (element?.regions || element?.locations || element?.tracks) {
                    if (regionMapFlag){
                        let regionMapItem: PlanningItem = element as PlanningItem;
                        regionMapItem.directoryRef = currentDirectory;
                        regionMapItem.kind = "regionMap";
                        currentDirectory.items.push(regionMapItem);
                    } else {
                        if(element.regions.length>0){
                            element.regions.forEach((regionRef: RegionRef) => {
                                let regionItem: PlanningItem = regionRef.region as PlanningItem;
                                regionItem.directoryRef = currentDirectory;
                                regionItem.kind = "region";
                                currentDirectory.items.push(regionItem);
                            });
                        }
                    }
                } else if (element?.allCommandList) {
                    let trajectoryItem: PlanningItem = element as PlanningItem;
                    trajectoryItem.directoryRef = currentDirectory;
                    trajectoryItem.kind = "apTrajectory";
                    currentDirectory.items.push(trajectoryItem);
                } else if (element?.mainTrajectory) {
                    let operationItem: PlanningItem = element as PlanningItem;
                    operationItem.directoryRef = currentDirectory;
                    operationItem.kind = "apOperation";
                    currentDirectory.items.push(operationItem);
                }
            }

            // In case the element is checked, we expand the parent directories
            if(element.isChecked){
                this.expandTree(element, tree)
            }

        });
        return this.orderDirectoryTree(tree);
    }

    /**
     * This method is used to order the tree depending on the parents/items and levels
     * @param tree
     * @returns
     */
    orderDirectoryTree(tree: Directory[]): Directory[] {

        let temporalDirectories = tree;

        function dfs(directory: Directory) {
            result.push(directory); // Add the current directory to the result
            const subdirectories = temporalDirectories.filter(
                (dir) => dir.parentRef === directory
            );
            // Sort the subdirectories by their level and then traverse each one
            subdirectories
                .sort((a, b) => a.level - b.level)
                .forEach(subdir => dfs(subdir));
        }

        const result: Directory[] = [];
        // Sort the root directories by their level and then perform DFS for each one
        temporalDirectories
            .filter(dir => dir.parentRef === null)
            .sort((a, b) => a.level - b.level)
            .forEach(root => dfs(root));

        return result;
    }

    /**
     * This emthod is used to expand all the parent directories of a checked element
     * @param element is the checked element
     * @param tree
     */
    expandTree(element: PlanningItem, tree: Directory[]){
        let parentRef = element.directoryRef;

        while(parentRef){
            tree.find((directory) => {
                (directory.name === parentRef.name && directory.level && parentRef.level) 
                    ? directory.isExpanded = !directory.isExpanded 
                    : null;
            })
            parentRef = parentRef.parentRef;
        }
    }
}
